import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import {
  ActionModal,
  AcknowledgeModal,
  Modal,
  Flex,
  useToast,
  Alert,
  Spacer,
  FormField,
  TextInput,
  CopyableValue,
  Typography
} from '@percent/lemonade'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'
import { useMutation } from '@percent/utility'
import { useAuthState } from '@percent/partner-dashboard/common/hooks'

type CreateKeySetModalProps = {
  open: boolean
  onClose: () => void
  refetchApiKeys: () => void
}

enum ModalState {
  CREATE_FORM = 'create',
  SUCCESS_SCREEN = 'success',
  CONFIRMATION = 'confirmation'
}

export function CreateKeySetModal({ open, onClose, refetchApiKeys }: CreateKeySetModalProps) {
  const { t } = useTranslation()
  const [value, setValue] = useState('')
  const [modalState, setModalState] = useState<ModalState>(ModalState.CREATE_FORM)
  const { partnerService } = useServices()
  const { partner } = useAuthState()
  const { addToast } = useToast()
  const [hasUserCopiedKey, setHasUserCopiedKey] = useState(false)

  const [{ data }, { apiFunc }] = useMutation(
    partnerService.createKeySet,
    () => {
      refetchApiKeys()
      setModalState(ModalState.SUCCESS_SCREEN)
    },
    () => {
      addToast(t('toast.createKeySetError'), 'error')
      onClose()
    }
  )

  const secretKey = useMemo(() => data?.data?.secretKey?.value, [data])
  const publishableKey = useMemo(() => data?.data?.publicKey?.value, [data])

  useEffect(() => {
    if (!open) {
      setModalState(ModalState.CREATE_FORM)
      setValue('')
      setHasUserCopiedKey(false)
    }
  }, [open])

  const handleSubmit = () => {
    if (partner?.id) {
      apiFunc({ partnerId: partner.id, name: value })
    }
  }

  const handleCloseSuccess = () => {
    setModalState(ModalState.CONFIRMATION)
  }

  const handleFinalClose = () => {
    setModalState(ModalState.CREATE_FORM)
    setValue('')
    setHasUserCopiedKey(false)
    onClose()
  }

  return (
    <Modal open={open} onClose={onClose}>
      {modalState === ModalState.CREATE_FORM && (
        <ActionModal
          title={t('button.createKeySet')}
          primaryButtonText={t('button.createKeySet')}
          primaryBtnTestId="create-key-set-btn"
          secondaryButtonText={t('button.cancel')}
          secondaryBtnTestId="cancel-key-set-btn"
          handleClose={onClose}
          handleSubmit={handleSubmit}
          disabled={!value}
        >
          <Flex direction="column" gap={16}>
            <Typography variant="default" $color="gray400">
              {t('keyList.createKeySet.description')}
            </Typography>
            <FormField label={t('keyList.createKeySet.label')} necessity="required">
              <TextInput
                name="name"
                placeholder={t('keyList.createKeySet.placeholder')}
                onChange={e => setValue(e.target.value)}
                value={value}
              />
            </FormField>
          </Flex>
        </ActionModal>
      )}
      {modalState === ModalState.SUCCESS_SCREEN && (
        <ActionModal
          title={t('button.createKeySet')}
          primaryButtonText={t('button.keyCopied')}
          primaryBtnTestId="close-success-btn"
          handleClose={handleCloseSuccess}
          handleSubmit={handleCloseSuccess}
          disabled={!hasUserCopiedKey}
        >
          <Alert variant="warning">{t('keyList.createKeySet.warning')}</Alert>
          <Spacer axis="vertical" size={4} />
          {secretKey && (
            <CopyableValue
              label={t('keyList.createKeySet.secretKey')}
              value={secretKey}
              description={t('keyList.createKeySet.copyDescription')}
              copiedMessage={t('toast.copied')}
              onCopy={() => setHasUserCopiedKey(true)}
              testId="secret-key"
            />
          )}
          <Spacer axis="vertical" size={4} />
          {publishableKey && (
            <CopyableValue
              label={t('keyList.createKeySet.publishableKey')}
              value={publishableKey}
              testId="publishable-key"
            />
          )}
        </ActionModal>
      )}
      {modalState === ModalState.CONFIRMATION && (
        <AcknowledgeModal
          result="positive"
          title={t('keyList.createKeySet.confirmation.title')}
          buttonText={t('button.close')}
          handleClose={handleFinalClose}
          buttonTestId="final-close-btn"
        />
      )}
    </Modal>
  )
}
